import {useCallback, useEffect, useMemo, useSyncExternalStore} from 'react';
import {
  type FrameSizeTransition,
  frameSizeTransitionStore,
} from './FrameSizeTransitionStore';
import {emptyArray} from '@packages/utilities/fp/utils';

/**
 * Read-only hook to access the active frame size transition for a given frame.
 * Use this if you're only interested in the current transition value to use it in the UI, and don't
 * need to update the transition.
 */
export const useFrameSizeTransition = (
  frameId: string
): FrameSizeTransition | null => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      return frameSizeTransitionStore.subscribe(frameId, onStoreChange);
    },
    [frameId]
  );

  const getSnapshot = useCallback(() => {
    return frameSizeTransitionStore.getSnapshot(frameId);
  }, [frameId]);

  const frameSizeTransition = useSyncExternalStore(subscribe, getSnapshot);
  return frameSizeTransition;
};

type ValueUpdaterFn = (
  currentValue: FrameSizeTransition
) => FrameSizeTransition;

type EndUpdateFn = (finalValue: FrameSizeTransition) => void;

type EndUpdateOptions = {
  /**
   * Specify the delay in ms to wait before clearing the transition. This is used as an escape hatch
   * to allow the transition value to fully be comitted to the main Flux store before clearing it.
   */
  delay?: number;
};

export type FrameSizeTransitionControlHandle = {
  /**
   * Starts a new transition with the given initial value. If a transition is already active, this is a no-op.
   */
  start: (
    initialValue: FrameSizeTransition,
    inheritingFrameIds?: string[]
  ) => void;
  /**
   * Ends the active transition and returns the final value. If no transition is active, this is a no-op.
   */
  end: (endUpdateFn: EndUpdateFn, options?: EndUpdateOptions) => void;
  /**
   * Updates the active transition with the given value. If no transition is active, this is a no-op.
   */
  update: (valueUpdaterFn: ValueUpdaterFn) => void;
};

/**
 * Returns a handle to start, update, and end a frame size transition for a given frame.
 * Use this if you need to initiate and control a transition in response to user input.
 */
export const useFrameSizeTransitionControl = (frameId: string) => {
  const handle: FrameSizeTransitionControlHandle = useMemo(() => {
    return {
      start: (
        initialValue: FrameSizeTransition,
        inheritingFrameIds: string[] = emptyArray
      ) => {
        const currentTransition = frameSizeTransitionStore.getSnapshot(frameId);
        if (!currentTransition) {
          frameSizeTransitionStore.startTransition(
            frameId,
            initialValue,
            inheritingFrameIds
          );
        }
      },
      end: (endUpdateFn: EndUpdateFn, options?: EndUpdateOptions) => {
        const finalValue = frameSizeTransitionStore.getSnapshot(frameId);
        if (finalValue) {
          endUpdateFn(finalValue);
        }

        if (options?.delay) {
          setTimeout(() => {
            frameSizeTransitionStore.endTransition(frameId);
          }, options.delay);
        } else {
          frameSizeTransitionStore.endTransition(frameId);
        }
      },
      update: (valueUpdaterFn: ValueUpdaterFn) => {
        const currentTransition = frameSizeTransitionStore.getSnapshot(frameId);
        if (currentTransition) {
          const nextValue = valueUpdaterFn(currentTransition);
          frameSizeTransitionStore.updateTransitionValue(frameId, nextValue);
        }
      },
    };
  }, [frameId]);

  useEffect(() => {
    return () => frameSizeTransitionStore.endTransition(frameId);
  }, [frameId]);

  return handle;
};

/**
 * Runs the provided callback whenever a `FrameSizeTransition` occurs in `FrameSizeTransitionStore`.
 */
export const useFrameSizeTransitionEffect = (callback: () => void) => {
  useEffect(() => {
    const unsubscribe = frameSizeTransitionStore.subscribeGlobal(callback);
    return () => {
      unsubscribe();
    };
  }, [callback]);
};
